import type { CommonPowerSyncDatabase } from '@powersync/common';
import { wipeLocalData } from './localDataWipe';
import { powersyncLogger } from './logger';

/**
 * Persistent record of which identity the local database was populated for.
 *
 * It cannot live in the database itself: `disconnectAndClear` clears local-only tables too, so the
 * record would be destroyed by the very wipe it exists to trigger. Each platform supplies its own
 * key-value store instead — `localStorage` on web, MMKV on native.
 */
export type SyncIdentityStore = {
  read: () => string | null;
  write: (identity: string) => void;
};

type SyncIdentity = {
  /** Clerk's user id. */
  userId: string;
  /** Clerk's organization id, or `null` for a record written before sync was org-scoped. */
  orgId: string | null;
};

type WipeLocalDataOnIdentityChangeOptions = {
  database: CommonPowerSyncDatabase;
  /** The signed-in user, or `null` when nobody is signed in. */
  userId: string | null;
  /** The active organization, or `null` when Clerk has not resolved one. */
  orgId: string | null;
};

/**
 * Neither id contains a colon — Clerk issues `user_…` and `org_…` — so this needs no escaping.
 * A stored value without one predates org scoping; see `parseIdentity`.
 */
const IDENTITY_SEPARATOR = ':';

const formatIdentity = ({ userId, orgId }: { userId: string; orgId: string }): string =>
  `${userId}${IDENTITY_SEPARATOR}${orgId}`;

/**
 * Records that the local database now holds `orgId`'s rows for `userId`.
 *
 * For a caller that has just wiped and reconnected on its own — an in-session organization switch —
 * so the next boot's `wipeLocalDataOnIdentityChange` finds a match instead of wiping again.
 */
export const recordSyncIdentity = ({
  store,
  userId,
  orgId,
}: {
  store: SyncIdentityStore;
  userId: string;
  orgId: string;
}): void => {
  store.write(formatIdentity({ userId, orgId }));
};

/** Whether the local database is already recorded as holding `orgId`'s rows for `userId`. */
export const hasSyncIdentity = ({
  store,
  userId,
  orgId,
}: {
  store: SyncIdentityStore;
  userId: string;
  orgId: string;
}): boolean => store.read() === formatIdentity({ userId, orgId });

const parseIdentity = (stored: string): SyncIdentity => {
  const separatorIndex = stored.indexOf(IDENTITY_SEPARATOR);

  if (separatorIndex === -1) {
    // Written by the user-only version of this check. The rows underneath it were selected by
    // `user_id`, which is not the set the org streams now deliver, so it cannot be trusted as a
    // match for any org.
    return { userId: stored, orgId: null };
  }

  return {
    userId: stored.slice(0, separatorIndex),
    orgId: stored.slice(separatorIndex + IDENTITY_SEPARATOR.length),
  };
};

/**
 * Wipes a local database that was populated for a different user, or for a different organization.
 *
 * Sign-out is not a reliable place to do this on the web. It navigates the tab away — cross-origin
 * to the Clerk primary domain, in satellite mode — while `disconnectAndClear` is deleting every
 * table, so the page can be torn down mid-wipe leaving the database half-cleared and nothing
 * running to finish it. Checking at startup needs no cooperation from the session that ended,
 * which also covers a session ended in another tab, on another device, or by a revoked token.
 *
 * Must be awaited *before* connecting. A wipe that starts after a connection is established clears
 * the SDK's pending options and leaves nothing to reconnect — the silent failure `connectVerified`
 * describes, which it can only recover from while it is still retrying.
 *
 * This runs once, on the first bootstrap. Switching organization inside a live session is handled
 * separately, by `switchOrganization`, which records the new identity itself.
 */
export const wipeLocalDataOnIdentityChange = async ({
  database,
  userId,
  orgId,
  store,
}: WipeLocalDataOnIdentityChangeOptions & { store: SyncIdentityStore }): Promise<void> => {
  if (!userId) {
    // Signed out, or Clerk has not resolved a user yet. The stored identity is deliberately left
    // alone: overwriting it here would cost the next signed-in boot its only way of telling that
    // the database changed hands.
    return;
  }

  const stored = store.read();
  const previous = stored ? parseIdentity(stored) : null;

  if (previous?.userId === userId && previous.orgId === orgId) {
    return;
  }

  // A different person is signing in. This is the case the check exists for, and it is decided on
  // the user alone — whether or not an organization has resolved yet, none of the previous
  // account's rows may be readable while the new one waits for its first sync.
  if (previous && previous.userId !== userId) {
    powersyncLogger.info('local database belongs to another user, wiping before sync');
    await wipeLocalData(database);
    store.write(orgId ? formatIdentity({ userId, orgId }) : userId);

    return;
  }

  if (!orgId) {
    // Same user, and Clerk has not named an active organization. Not a change — an absence. Wiping
    // on it would clear the database on any boot that reaches this point before the organization
    // resolves, and rewriting the record would lose the organization the rows actually belong to.
    // The rows themselves are still this user's, so nothing is exposed by leaving them.
    return;
  }

  if (previous) {
    // Same user, different organization — or a record from before sync was org-scoped, whose rows
    // were chosen by a predicate this organization's streams do not reproduce.
    powersyncLogger.info(
      previous.orgId
        ? 'local database belongs to another organization, wiping before sync'
        : 'local database predates org-scoped sync, wiping before sync',
    );
  } else {
    // Either a first run, where the wipe is a no-op on an empty database, or a database written
    // before this check existed. The second case is indistinguishable from the first and cannot be
    // attributed to anyone, so it is wiped rather than trusted — re-syncing costs less than showing
    // someone another account's boats.
    powersyncLogger.info('local database has no recorded owner, wiping before sync');
  }

  // Changes still waiting to upload outweigh a precautionary wipe, which would discard them with the
  // queue. Only where the rows are this user's own: a legacy record is by definition, and a database
  // with no record and an upload queue is one this user has been writing to — sign-out wipes on
  // mobile, and the web has recorded an owner since before sync was org-scoped. Their rows reconcile
  // once the org streams connect, and the next boot wipes once the queue is empty.
  if (!previous?.orgId) {
    const { count } = await database.getUploadQueueStats();

    if (count > 0) {
      powersyncLogger.info('deferring the wipe until pending changes have uploaded', { count });
      return;
    }
  }

  await wipeLocalData(database);

  store.write(formatIdentity({ userId, orgId }));
};
